import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  acknowledgeSLAAlert,
  createSilence,
  fetchSLADashboard,
  fetchSLAReport,
  downloadBlob,
  type SLADashboard,
  type SLAModuleCard,
  type SLAWindow,
} from '../services/slaService';
import { typography } from '~/theme';

const WINDOWS: { value: SLAWindow; label: string }[] = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

const WINDOW_DAYS: Record<SLAWindow, number> = { '24h': 1, '7d': 7, '30d': 30 };

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'healthy': return 'success';
    case 'degraded': return 'warning';
    case 'down': return 'error';
    default: return 'default';
  }
};

const ModuleCard: React.FC<{ card: SLAModuleCard; onSilence: (module: string) => void }> = ({ card, onSilence }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={typography.label.fontWeight}>
          {card.module?.toUpperCase() ?? 'UNKNOWN'}
        </Typography>
        <Chip label={card.status ?? 'unknown'} color={statusColor(card.status ?? '')} size="small" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Uptime: {(card.metrics?.uptime_percent)?.toFixed(3) ?? '-'}% / {card.targets?.uptime_target_percent ?? '-'}%
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        p95 Latency: {(card.metrics?.p95_ms)?.toFixed(1) ?? '-'}ms / {card.targets?.response_time_p95_ms ?? '-'}ms
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Error Rate: {(card.metrics?.error_rate_percent)?.toFixed(3) ?? '-'}% / {card.targets?.error_rate_percent ?? '-'}%
      </Typography>
      {card.breached && (
        <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
          {(card.breach_reasons ?? []).join('; ')}
        </Alert>
      )}
      <Box sx={{ mt: 1, textAlign: 'right' }}>
        <Button size="small" disabled={!card.module} onClick={() => card.module && onSilence(card.module)}>Silence</Button>
      </Box>
    </CardContent>
  </Card>
);

const SLADashboardTab: React.FC = () => {
  const [window, setWindow] = useState<SLAWindow>('24h');
  const [data, setData] = useState<SLADashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ackLoading, setAckLoading] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dashboard = await fetchSLADashboard(window);
      setData(dashboard);
    } catch (err) {
      setError('Failed to load SLA dashboard.');
    } finally {
      setLoading(false);
    }
  }, [window]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const handleAck = async (alertId: string) => {
    setAckLoading(alertId);
    try {
      await acknowledgeSLAAlert(alertId);
      await load();
    } catch {
      setError('Failed to acknowledge alert.');
    } finally {
      setAckLoading(null);
    }
  };

  const handleSilence = async (module: string) => {
    try {
      await createSilence({ module, duration_minutes: 60, reason: 'Planned maintenance' });
      await load();
    } catch {
      setError('Failed to create silence.');
    }
  };

  const handleDownloadReport = async (format: 'csv' | 'pdf') => {
    try {
      const blob = await fetchSLAReport(undefined, undefined, WINDOW_DAYS[window], format) as Blob;
      const ext = format === 'pdf' ? 'pdf' : 'csv';
      const mime = format === 'pdf' ? 'application/pdf' : 'text/csv';
      const fileBlob = blob instanceof Blob ? blob : new Blob([blob], { type: mime });
      downloadBlob(fileBlob, `sla_report_${new Date().toISOString().slice(0, 10)}.${ext}`);
    } catch {
      setError(`Failed to download SLA ${format.toUpperCase()} report.`);
    }
  };

  const resolvedAlerts = data?.breach_history?.filter((alert) => alert.status === 'resolved') ?? [];

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ flex: 1 }}>SLA Dashboard</Typography>
        {data && data.overall_status && (
          <Chip
            label={`Overall: ${data.overall_status.toUpperCase()}`}
            color={statusColor(data.overall_status)}
            size="small"
          />
        )}
        {loading && !data && <CircularProgress size={16} />}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Window</InputLabel>
          <Select value={window} label="Window" onChange={(e) => setWindow(e.target.value as SLAWindow)}>
            {WINDOWS.map((w) => (
              <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Refresh now">
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Tooltip>
        <Tooltip title="Download CSV report">
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleDownloadReport('csv')}>
            CSV
          </Button>
        </Tooltip>
        <Tooltip title="Download PDF report">
          <Button size="small" variant="outlined" startIcon={<PdfIcon />} onClick={() => handleDownloadReport('pdf')}>
            PDF
          </Button>
        </Tooltip>
        {loading && <CircularProgress size={16} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {data?.modules?.map((m) => (
          <Grid item xs={12} sm={6} md={4} key={m.module}>
            <ModuleCard card={m} onSilence={handleSilence} />
          </Grid>
        ))}
      </Grid>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Active Alerts</Typography>
      <TableContainer component={Paper} sx={{ mb: 3, minHeight: 200 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Module / Service</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!data || (data.active_alerts?.length ?? 0) === 0) ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No active alerts
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.active_alerts.map((alert) => (
                <TableRow key={alert.alert_id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {alert.created_at ? new Date(alert.created_at).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip label={alert.severity ?? 'unknown'} color={(alert.severity ?? '') === 'critical' ? 'error' : 'warning'} size="small" />
                  </TableCell>
                  <TableCell>{alert.module ?? '-'} / {alert.service_name ?? '-'}</TableCell>
                  <TableCell>{alert.message ?? '-'}</TableCell>
                  <TableCell>{alert.status ?? '-'}</TableCell>
                  <TableCell align="right">
                    {(alert.status ?? '') === 'firing' ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleAck(alert.alert_id)}
                        disabled={ackLoading === alert.alert_id}
                      >
                        Acknowledge
                      </Button>
                    ) : (
                      <Chip label="Acknowledged" size="small" color="info" />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Breach History</Typography>
      <TableContainer component={Paper} sx={{ flex: 1, minHeight: 200 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Module / Service</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Resolved</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!data || resolvedAlerts.length === 0) ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No breach history
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              resolvedAlerts.map((alert) => (
                <TableRow key={alert.alert_id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {alert.created_at ? new Date(alert.created_at).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip label={alert.severity ?? 'unknown'} color={(alert.severity ?? '') === 'critical' ? 'error' : 'warning'} size="small" />
                  </TableCell>
                  <TableCell>{alert.module ?? '-'} / {alert.service_name ?? '-'}</TableCell>
                  <TableCell>{alert.message ?? '-'}</TableCell>
                  <TableCell>{alert.status ?? '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {alert.resolved_at ? new Date(alert.resolved_at).toLocaleString() : '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SLADashboardTab;
